import { ChevronRight, Dots, Mail, NetworkIcon, Plus, SendIcon } from 'assets/icons';
import Button from 'components/button';
import CardIconButton from 'components/card/CardIconButton';
import MenuButton from 'components/DropdownMenu/MenuButton';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import PillTabs from 'components/tabs/PillTabs';
import { companyRoleLabel } from 'constants/companyRoles';
import useText from 'hooks/useText';
import { useState } from 'react';
import { LIST_GROUP_HEADER_CLASS } from 'utils/surfaceClasses';
import ContactRow from './ContactRow';
import MemberRoleDialog from './MemberRoleDialog';
import { RecordSection } from './RecordLayout';

// The header of a group of contacts, across the whole card as the groups of
// the home page's tasks: the first one takes the card's top corners.
const GroupHead = ({ first, children }) => (
  <p className={`-mx-4 border-b border-gray-100 ${LIST_GROUP_HEADER_CLASS} ${first ? '-mt-4 rounded-t-xl' : 'border-t'}`}>{children}</p>
);

/**
 * The « Contacts » of a company, as the admin's tab: its members registered
 * on Jobbiz (an invitation pending can be sent again), then the company's own
 * contacts without an account (`unregistered`, they are invited); each opens
 * their page (their id). Then the contacts
 * of its group, read only: they are managed on the group. Without `onAdd`, no
 * contact is added; without `onInvite`, no « Inviter » (a prospect's contacts
 * are invited as a client's). With `onRoleChanged`,
 * the « ⋯ » of a member changes their Jobbiz role in `companyId`, then calls it.
 * A position not given reads « - ».
 */
const CompanyContacts = ({
  companyId,
  members,
  unregistered,
  group,
  groupContacts,
  onOpen,
  onAdd,
  onInvite,
  onResend,
  onRoleChanged,
  onOpenGroup,
  onOpenGroupContact,
}) => {
  const { text, language } = useText('record');
  const [roleOf, setRoleOf] = useState(null);
  const detail = (person) =>
    [
      person.position || '-',
      person.account === 'active' && person.role && text('access', { role: companyRoleLabel(language, person.role) }),
      person.phone,
      person.email,
    ]
      .filter(Boolean)
      .join(' · ');
  const [view, setView] = useState('all');
  const count = members.length + unregistered.length;
  const showMembers = view !== 'notRegistered' && members.length > 0;
  const showUnregistered = view !== 'registered' && unregistered.length > 0;
  // Every tab keeps the header of its group, with its count.
  return (
    <div className="flex flex-col gap-4 pb-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <PillTabs
            activeTab={view}
            onChange={setView}
            tabs={[
              { id: 'all', label: 'Tous', count },
              { id: 'registered', label: 'Inscrit sur Jobbiz', count: members.length },
              { id: 'notRegistered', label: 'Non inscrit', count: unregistered.length },
            ]}
          />
          {onAdd && (
            <Button color="SECONDARY" size="SM" onClick={onAdd}>
              <Plus wh={18} color="currentColor" />
              <span>{text('addContact')}</span>
            </Button>
          )}
        </div>
        {/* The rows carry their own 10 px above and under them: the card only
            adds 6 to end on 16, and to start on 16 when no group header
            takes its top. */}
        <RecordSection className={showMembers || showUnregistered ? 'pb-1.5' : ''}>
          {!count && <CompactEmptyState compact glyph="inbox" title={text('contactsEmptyTitle')} description={text('contactsEmptyText')} />}
          {count > 0 && !showMembers && !showUnregistered && (
            <CompactEmptyState
              compact
              glyph="inbox"
              title={view === 'registered' ? 'Aucun contact inscrit sur Jobbiz' : 'Aucun contact non inscrit'}
            />
          )}
          {showMembers && (
            <div>
              <GroupHead first>{text('registered', { count: members.length })}</GroupHead>
              {members.map((person) => (
                <ContactRow
                  key={person.id}
                  person={person}
                  detail={detail(person)}
                  onOpen={() => onOpen(person)}
                  actions={
                    <>
                      {person.account === 'pending' && (
                        <CardIconButton label={text('resend')} icon={<Mail wh={18} color="#374151" />} onClick={() => onResend(person)} />
                      )}
                      {onRoleChanged && (
                        <MenuButton
                          trigger={(toggleMenu) => (
                            <CardIconButton
                              label={text('memberActions', { name: [person.firstname, person.lastname].filter(Boolean).join(' ') })}
                              icon={<Dots w={16} h={4} color="#374151" />}
                              onClick={toggleMenu}
                            />
                          )}
                          items={[{ label: text('changeRole'), onClick: () => setRoleOf(person) }]}
                        />
                      )}
                    </>
                  }
                />
              ))}
            </div>
          )}
          {showUnregistered && (
            <div>
              <GroupHead first={!showMembers}>{text('notRegistered', { count: unregistered.length })}</GroupHead>
              {unregistered.map((person) => (
                <ContactRow
                  key={person.id}
                  person={person}
                  detail={detail(person)}
                  onOpen={() => onOpen(person)}
                  actions={
                    onInvite && (
                      <Button color="SECONDARY" size="SM" onClick={() => onInvite(person)}>
                        <SendIcon wh={16} color="currentColor" />
                        <span>{text('invite')}</span>
                      </Button>
                    )
                  }
                />
              ))}
            </div>
          )}
        </RecordSection>
      </div>
      {roleOf && (
        <MemberRoleDialog
          companyId={companyId}
          member={roleOf}
          onClose={() => setRoleOf(null)}
          onSaved={() => {
            setRoleOf(null);
            onRoleChanged();
          }}
        />
      )}
      {group && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-900">{text('groupContacts', { count: groupContacts.length })}</p>
            <Button color="SECONDARY" size="SM" onClick={onOpenGroup}>
              <span>{group.name}</span>
              <ChevronRight wh={16} color="currentColor" />
            </Button>
          </div>
          <RecordSection className={groupContacts.length ? 'py-1.5' : ''}>
            {/* The small, still one: the section is a card of the tab. */}
            {!groupContacts.length && (
              <CompactEmptyState icon={NetworkIcon} title="Aucun contact du groupe" description={text('groupContactsEmpty')} />
            )}
            {groupContacts.map((person) => (
              <ContactRow
                key={person.id}
                person={person}
                detail={[person.position, person.phone, person.email].filter(Boolean).join(' · ')}
                onOpen={() => onOpenGroupContact(person)}
              />
            ))}
          </RecordSection>
        </div>
      )}
    </div>
  );
};

export default CompanyContacts;
